import { match } from 'ts-pattern';
import { Position } from '../hooks/usePositions';
import { OrderSide } from '../types/generated';
import { Gwei } from './gwei';
import { alignToValidSize, multiply } from './math';

/**
 * Calculate the price for a stop loss or take profit order based on percentage
 */
export const calcPriceFromPct = ({
  percent,
  side,
  tickSize,
  avgPrice,
  isStopLoss,
}: {
  percent: bigint;
  side: Position['side'];
  avgPrice: Position['avgPrice'];
  tickSize: bigint;
  isStopLoss: boolean;
}): bigint => {
  const percentAsDecimal = percent / 100n;
  const baseMultiplier = Gwei.toGwei(1);

  const multiplier = match({ isLong: side === OrderSide.BUY, isStopLoss })
    .with({ isLong: true, isStopLoss: true }, () => baseMultiplier - percentAsDecimal) // Long SL: price down
    .with({ isLong: true, isStopLoss: false }, () => baseMultiplier + percentAsDecimal) // Long TP: price up
    .with({ isLong: false, isStopLoss: true }, () => baseMultiplier + percentAsDecimal) // Short SL: price up
    .with({ isLong: false, isStopLoss: false }, () => baseMultiplier - percentAsDecimal) // Short TP: price down
    .exhaustive();

  const price = multiply(avgPrice, multiplier);

  return alignToValidSize(Gwei.max(price, tickSize), tickSize);
};

/**
 * Calculate the percentage of the price from the price
 * @param price - The price to calculate the percentage from
 * @param avgPrice - The average price of the position
 * @returns The percentage of the price from the avgPrice
 * @example
 * calcPctFromPrice({ price: 100n, avgPrice: 100n }) // 0n
 * calcPctFromPrice({ price: 110n, avgPrice: 100n }) // 10n
 * calcPctFromPrice({ price: 90n, avgPrice: 100n }) // -10n
 */
export const calcPctFromPrice = ({
  side,
  price,
  avgPrice,
}: {
  price: bigint;
  avgPrice: bigint;
  side: Position['side'];
}) => {
  if (avgPrice === 0n) return 0n;

  const priceDifference = side === OrderSide.BUY ? price - avgPrice : avgPrice - price;
  return (priceDifference * Gwei.toGwei(100)) / avgPrice;
};
